/**
 * Where a letter is, and when it arrives, in words — derived only from the
 * server's own timestamps (`collectedAt`, `deliverAt`), never guessed.
 *
 * The letter's *state* is not a clock: since collections, `in_transit` spans
 * the whole journey from the van to the letterbox, so a stage read off the
 * state ("in_transit → arriving any moment") would say "any moment" for a
 * letter due tomorrow morning. Every phrase here is computed from time.
 */

import type { LetterState, PostageClass } from '@toiyp/shared';

/** Within this of `deliverAt`, a letter is honestly "any minute". */
export const ANY_MINUTE_MS = 5 * 60 * 1000;

/** The stage of a letter's round, in the post office's own words. */
export function postStage({
  now,
  collectedAt,
  deliverAt,
  state,
  postageClass = 'second',
}: {
  now: number;
  collectedAt: number | null;
  deliverAt: number;
  state: LetterState;
  postageClass?: PostageClass;
}): string {
  // Due already — the sweep (every 30 s) is about to put it through the door.
  if (now >= deliverAt || state === 'delivered') return 'Arriving any moment';
  if (deliverAt - now <= ANY_MINUTE_MS) return 'Arriving any minute';
  // Express is collected at sealing: no postbox, no sorting office — one run.
  if (postageClass === 'express') return 'Out for express delivery';
  if (!collectedAt || now < collectedAt) return 'In the postbox';
  const transitMs = Math.max(1, deliverAt - collectedAt);
  const p = Math.min(1, Math.max(0, (now - collectedAt) / transitMs));
  if (p < 0.35) return 'Collected from the postbox';
  if (p < 0.75) return 'At the sorting office';
  return 'Out for delivery';
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** "9am", "4:30pm" — the exact minute, on a 12-hour clock. */
export function clockTime(d: Date): string {
  const h = d.getHours();
  const m = d.getMinutes();
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}${m === 0 ? '' : `:${String(m).padStart(2, '0')}`}${h < 12 ? 'am' : 'pm'}`;
}

/**
 * "any minute", "today at 4:30pm", "tomorrow at 9am", "Thursday at 9am", or a
 * date for anything further out — always the server's `deliverAt`.
 */
export function arrivesPhrase(deliverAt: string | number | Date, now: Date = new Date()): string {
  const at = new Date(deliverAt);
  if (at.getTime() - now.getTime() <= ANY_MINUTE_MS) return 'any minute';
  const time = clockTime(at);
  if (sameDay(at, now)) return `today at ${time}`;
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  if (sameDay(at, tomorrow)) return `tomorrow at ${time}`;
  const inAWeek = new Date(now);
  inAWeek.setDate(now.getDate() + 6);
  if (at.getTime() <= inAWeek.getTime()) {
    return `${at.toLocaleDateString([], { weekday: 'long' })} at ${time}`;
  }
  return `${at.toLocaleDateString([], { day: 'numeric', month: 'short' })} at ${time}`;
}
